import React, { useState, useContext } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import parse from 'html-react-parser';
import { useNavigate, useParams } from 'react-router-dom';

import { neutral } from '_lex/_foundations/phxColors';
import { translate as t } from 'hooks/useTranslations';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';
import { DTBLibraryContext } from '../../context';
import type { ContentReference } from '../../../_types/dtb';
import { usePolicyReferenceResolver } from '../../_utils/policyReferenceUtils';

const PolicyReferenceBlock: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { editedData, handleSave } = useContext(DTBLibraryContext);

  const [isCollapsed, setIsCollapsed] = useState(false);

  const contentReferences: ContentReference[] = editedData?.contentReferences ?? [];
  const { resolvedRefs, isLoading } = usePolicyReferenceResolver(contentReferences);

  const handleLinkClick = async () => {
    if (!id) return;
    await handleSave(false);
    navigate(`/admin/dtbs/library/${id}/content-reference`);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Stack spacing={2}>
          <Skeleton variant='rectangular' height={20} width='40%' />
          <Skeleton variant='text' />
          <Skeleton variant='text' />
          <Skeleton variant='text' width='80%' />
        </Stack>
      );
    }

    if (!contentReferences.length) {
      return (
        <Button
          variant='text'
          size='small'
          onClick={handleLinkClick}
          disabled={!editedData?.manual?.id}
          sx={{ px: 0 }}
        >
          {t('dtbs.library.detail.policyReferenceBlock.linkPrompt', 'Link a Policy Reference')}
        </Button>
      );
    }

    return (
      <Stack spacing={10} divider={<Divider />}>
        {resolvedRefs.map((ref, index) => (
          <Stack key={`${ref.name}_${index}`} spacing={1}>
            <Typography variant='subtitle1' fontWeight={600}>{ref.name}</Typography>
            {ref.content && (
              <Typography variant='body1' component='div'>
                {parse(ref.content, {
                  replace(node: any, ind: number) {
                    return PolicyContentHelper.handleContent(node, ind);
                  },
                })}
              </Typography>
            )}
          </Stack>
        ))}
      </Stack>
    );
  };

  return (
    <Card className='lex-card'>
      <CardHeader
        title={t('dtbs.library.detail.policyReferenceBlock.title', 'Policy Reference')}
        action={
          <Button
            variant='text'
            color='neutral'
            size='small'
            className='xs icon-only'
            onClick={() => setIsCollapsed((prev) => !prev)}
          >
            {isCollapsed ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
          </Button>
        }
      />
      {!isCollapsed && (
        <CardContent sx={{ paddingX: 6 }}>
          {renderContent()}
        </CardContent>
      )}
    </Card>
  );
};

export default PolicyReferenceBlock;
